/**
 * HQ 가맹점 관리 서비스.
 */
import hqApi from './hqApiClient';

export interface HqStore {
  id: string;
  name: string;
  region?: string | null;
  seat_count?: number | null;
  area_pyeong?: number | null;
  student_count: number;
  voucher_price?: number | null;
  owner_name?: string | null;
  owner_phone?: string | null;
  owner_email?: string | null;
  contract_started_at?: string | null;
  contract_ended_at?: string | null;
  franchise_status: 'franchisee' | 'direct' | 'standalone';
  franchise_fee?: number | null;
  franchise_fee_paid_at?: string | null;
  notes?: string | null;
  occupancy_rate?: number | null;
  status_color: 'green' | 'yellow' | 'red';
  // 구독·계정 상태 (구독형 운영 콘솔)
  subscription_plan?: string | null;
  subscription_status?: string | null; // trial | active | suspended | expired
  account_status?: string | null; // approved | suspended
  plan_started_at?: string | null;
  plan_ends_at?: string | null;
  deploy_mode?: string | null;
  customer_type?: 'sale' | 'subscription';
  // create 응답 시에만 채워지는 임시 비번 안내 (목록 조회 시 X)
  temp_password?: string;
  login_email?: string;
  user_number?: string;
}

export interface HqStoreCreate {
  name: string;
  region?: string;
  seat_count?: number;
  area_pyeong?: number;
  voucher_price?: number;
  owner_name?: string;
  owner_phone?: string;
  owner_email?: string;
  contract_started_at?: string;
  franchise_fee?: number;
  franchise_fee_paid_at?: string;
  franchise_status?: 'franchisee' | 'direct';
  notes?: string;
}

export const hqStoreService = {
  async list(status?: string): Promise<HqStore[]> {
    const { data } = await hqApi.get<HqStore[]>('/api/v1/hq/stores', {
      params: status ? { status } : undefined,
    });
    return data;
  },

  async get(id: string): Promise<HqStore> {
    const { data } = await hqApi.get<HqStore>(`/api/v1/hq/stores/${id}`);
    return data;
  },

  async create(payload: HqStoreCreate): Promise<HqStore> {
    const { data } = await hqApi.post<HqStore>('/api/v1/hq/stores', payload);
    return data;
  },

  async update(id: string, payload: Partial<HqStoreCreate>): Promise<HqStore> {
    const { data } = await hqApi.put<HqStore>(`/api/v1/hq/stores/${id}`, payload);
    return data;
  },

  async alimtalkCredit(id: string): Promise<HqAlimtalkCredit> {
    const { data } = await hqApi.get<{ success: boolean; credit: HqAlimtalkCredit }>(
      `/api/v1/hq/stores/${id}/alimtalk-credit`,
    );
    return data.credit;
  },
};

// 카톡 알림톡 크레딧 요약 (점주 화면과 동일 소스)
export interface HqAlimtalkCredit {
  balance: number;            // 유료 잔액(원)
  unit_price: number;         // 건당 단가(원, 기본 13)
  free_quota: number;
  free_used: number;
  free_remaining: number;     // 무료 잔여(건)
  paid_remaining_count: number; // 유료로 보낼 수 있는 건수
  remaining_count: number;    // 무료+유료 합계 발송 가능 건수
  prepaid_enabled: boolean;   // 선불 모드(소진 시 발송 중단)
  free_month?: string | null;
}

// ===== 구독·청구 =====
export interface HqBillingInfo {
  organization: {
    id: string;
    name: string;
    customer_type: 'sale' | 'subscription';
    subscription_plan: string | null;
    subscription_status: string | null;
    account_status: string | null;
    plan_started_at: string | null;
    plan_ends_at: string | null;
    suspended_reason: string | null;
  };
  year_month: string;
  subscription: any;
  payment_summary: any;
  outstanding: any;
  payments: any[];
}

export const hqBillingService = {
  async get(orgId: string, yearMonth?: string): Promise<HqBillingInfo> {
    const { data } = await hqApi.get<HqBillingInfo>(
      `/api/v1/hq/stores/${orgId}/billing`,
      { params: yearMonth ? { year_month: yearMonth } : {} },
    );
    return data;
  },
  async recordPayment(
    orgId: string,
    yearMonth: string,
    body: { amount: number; payment_type?: string; notes?: string },
  ) {
    const { data } = await hqApi.post(
      `/api/v1/hq/stores/${orgId}/payments/${yearMonth}`,
      body,
    );
    return data;
  },
  async changeSubscription(
    orgId: string,
    body: { action: string; plan?: string; months?: number; reason?: string },
  ) {
    const { data } = await hqApi.post(
      `/api/v1/hq/stores/${orgId}/subscription`,
      body,
    );
    return data;
  },
};

// ─── 대시보드 ────────────────────────────────────────────────
export interface HqDashboardSummary {
  active_stores: number;
  franchisee_count: number;
  direct_count: number;
  target_stores: number;
  total_students: number;
  estimated_monthly_royalty: number;
  settlement_month: string;
}

export interface HqDashboardTopStore {
  id: string;
  name: string;
  seat_count?: number | null;
  student_count: number;
  occupancy_rate?: number | null;
  royalty_amount: number;
}

export interface HqDashboardStats {
  summary: HqDashboardSummary;
  top_stores: HqDashboardTopStore[];
}

export const hqDashboardService = {
  async stats(): Promise<HqDashboardStats> {
    const { data } = await hqApi.get<HqDashboardStats>('/api/v1/hq/dashboard/stats');
    return data;
  },

  async royaltyPreview(): Promise<{
    settlement_month: string;
    total_royalty: number;
    stores: any[];
  }> {
    const { data } = await hqApi.get('/api/v1/hq/dashboard/royalty-preview');
    return data;
  },
};
